// Typed fetch wrappers around the Netlify functions. Owner requests send the session cookie; canner
// requests carry the token via ?ct= (added by the caller).
import type { BusinessData, DemandSnapshot, Registry, Receipt, RunStatus, StockLot } from "@shared/types.ts";

const BASE = "/.netlify/functions";

export class ApiError extends Error {
  constructor(message: string, public status: number, public body: unknown) {
    super(message);
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}/${path}`, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    ...init,
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError((j as { error?: string }).error || res.statusText, res.status, j);
  return j as T;
}

export interface OwnerState { role: "owner"; registry: Registry; data: BusinessData | null }
export interface CannerState { role: "canner"; business: { id: string; name: string }; cannerId: string; data: BusinessData }

export const api = {
  // auth
  authStatus: () => call<{ authed: boolean }>("login", { method: "GET" }),
  login: (password: string) => call<{ ok: boolean }>("login", { method: "POST", body: JSON.stringify({ password }) }),
  logout: () => call<{ ok: boolean }>("login", { method: "DELETE" }),

  // owner state
  state: (businessId?: string) =>
    call<OwnerState>(`state${businessId ? `?business=${encodeURIComponent(businessId)}` : ""}`, { method: "GET" }),
  saveBusiness: (data: BusinessData) => call<{ ok: boolean }>("save-business", { method: "POST", body: JSON.stringify({ data }) }),
  deleteBusiness: (id: string) => call<{ ok: boolean }>("delete-business", { method: "POST", body: JSON.stringify({ id }) }),
  seed: () => call<{ ok: boolean; seeded: boolean; businessId: string }>("seed", { method: "POST" }),

  // demand planning (Shopify + Amazon)
  demand: (businessId: string, refresh?: boolean) =>
    call<DemandSnapshot>(`demand?business=${encodeURIComponent(businessId)}${refresh ? "&refresh=1" : ""}`, { method: "GET" }),
  // Amazon SP-API report job (poll until ready)
  amazon: (businessId: string, refresh?: boolean) =>
    call<{ configured: boolean; ready?: boolean; pending?: boolean; at?: number; skus?: number; note?: string; error?: string; detail?: { sellerSku: string; inv: number; monthly: number; mapped?: boolean }[] }>(
      `amazon?business=${encodeURIComponent(businessId)}${refresh ? "&refresh=1" : ""}`, { method: "GET" }),
  // per-SKU × per-channel live data for Distribution
  channels: (businessId: string) =>
    call<ChannelsMatrix>(`channels?business=${encodeURIComponent(businessId)}`, { method: "GET" }),

  // canner state (token-scoped)
  cannerState: (token: string) => call<CannerState>(`state?ct=${encodeURIComponent(token)}`, { method: "GET" }),

  // shared mutations (owner or canner). token optional → appended as ?ct=
  receive: (body: { businessId: string; orderId: string; lineId: string; receipt: Receipt; addToStock?: boolean }, token?: string) =>
    call<{ ok: boolean; orderStatus: string }>(`receive${token ? `?ct=${encodeURIComponent(token)}` : ""}`, { method: "POST", body: JSON.stringify(body) }),
  stock: (body: { businessId: string; action: "add" | "update" | "delete"; lot: Partial<StockLot> }, token?: string) =>
    call<{ ok: boolean; stock: StockLot[] }>(`stock${token ? `?ct=${encodeURIComponent(token)}` : ""}`, { method: "POST", body: JSON.stringify(body) }),
  complete: (body: { businessId: string; runId: string; items: { skuId: string; made: number; sent: number; kept: number }[]; notes?: string }, token?: string) =>
    call<{ ok: boolean; manufacturerStock: Record<string, number> }>(`complete${token ? `?ct=${encodeURIComponent(token)}` : ""}`, { method: "POST", body: JSON.stringify(body) }),
  runStatus: (body: { businessId: string; runId: string; status: RunStatus }, token?: string) =>
    call<{ ok: boolean; status: string }>(`run-status${token ? `?ct=${encodeURIComponent(token)}` : ""}`, { method: "POST", body: JSON.stringify(body) }),
  runSchedule: (body: { businessId: string; runId: string; action: "propose" | "confirm"; date?: string; note?: string }, token?: string) =>
    call<{ ok: boolean }>(`run-schedule${token ? `?ct=${encodeURIComponent(token)}` : ""}`, { method: "POST", body: JSON.stringify(body) }),
  runUpdate: (body: { businessId: string; runId: string; patch: { scheduledDate?: string; readyDate?: string; poNumber?: string; batchNumber?: string; notes?: string; packagingReceived?: boolean; componentsReceived?: string[]; componentShipment?: { name: string; shipment: { carrier?: string; trackingNumber?: string; shippedDate?: string; expectedDelivery?: string; note?: string } }; delay?: { toDate: string; reason?: string } } }, token?: string) =>
    call<{ ok: boolean }>(`run-update${token ? `?ct=${encodeURIComponent(token)}` : ""}`, { method: "POST", body: JSON.stringify(body) }),
  nudge: (body: { businessId: string; runId?: string; title?: string; message: string }) =>
    call<{ ok: boolean; emailed: boolean; recipients: number }>("nudge", { method: "POST", body: JSON.stringify(body) }),
  batchNotes: (body: { businessId: string; skuId: string; notes: string }, token?: string) =>
    call<{ ok: boolean }>(`batch-notes${token ? `?ct=${encodeURIComponent(token)}` : ""}`, { method: "POST", body: JSON.stringify(body) }),
  labelCheck: (businessId: string, skuId: string) =>
    call<{ at: number; status: "pass" | "issues" | "error"; summary?: string; issues: { severity: "error" | "warning"; text: string }[] }>("label-check", { method: "POST", body: JSON.stringify({ businessId, skuId }) }),
  upload: (businessId: string, dataUrl: string, token?: string) =>
    call<{ key: string; url: string }>(`u${token ? `?ct=${encodeURIComponent(token)}` : ""}`, { method: "POST", body: JSON.stringify({ businessId, dataUrl }) }),
  notifRead: (body: { businessId: string; ids?: string[] }, token?: string) =>
    call<{ ok: boolean }>(`notif-read${token ? `?ct=${encodeURIComponent(token)}` : ""}`, { method: "POST", body: JSON.stringify(body) }),
  notifyTest: (body: { businessId: string; audience?: "owner" | "mfr" }, token?: string) =>
    call<{ ok: boolean; skipped?: boolean; status?: number; error?: string; audience: string; to: number }>(`notify-test${token ? `?ct=${encodeURIComponent(token)}` : ""}`, { method: "POST", body: JSON.stringify(body) }),
};

export interface ChannelCell { invPacks: number; packsMo: number; source: "shopify" | "amazon" | "manual"; pending?: boolean }
export interface ChannelsMatrix {
  at: number;
  channels: { id: string; name: string; kind: string; auto: boolean }[];
  skus: { skuId: string; name: string; packSize: number; assembled: boolean }[];
  matrix: Record<string, Record<string, ChannelCell>>;
}

export function photoSrc(key: string, token?: string): string {
  return `${BASE}/r?k=${encodeURIComponent(key)}${token ? `&ct=${encodeURIComponent(token)}` : ""}`;
}
